import { useAccountingCompany } from '../components/HoldingAccounting';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function FinancialPayees() {
  const companyId = useAccountingCompany();
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const base = `${API}/companies/${companyId}/finance`;

  const [payees, setPayees] = useState([]);
  const [payeeTypes, setPayeeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ name: '', payee_type_id: '', email: '', phone: '', address: '', bank_details: '', notes: '' });

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      fetch(`${base}/payees`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
      fetch(`${base}/settings/payee-types`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : []),
    ]).then(([p, types]) => {
      setPayees(Array.isArray(p) ? p : []);
      setPayeeTypes(Array.isArray(types) ? types : []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { if (companyId) fetchAll(); }, [companyId]);

  const savePayee = async () => {
    const url = editing ? `${base}/payees/${editing.id}` : `${base}/payees`;
    const method = editing ? 'PUT' : 'POST';
    const res = await fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    if (res.ok) { setShowForm(false); setEditing(null); setForm({ name: '', payee_type_id: '', email: '', phone: '', address: '', bank_details: '', notes: '' }); fetchAll(); }
  };

  const viewDetail = async (id) => {
    const res = await fetch(`${base}/payees/${id}`, { headers: getAuthHeaders() });
    if (res.ok) { const d = await res.json(); setDetail(d); }
  };

  const editPayee = (p) => { setEditing(p); setForm({ name: p.name, payee_type_id: p.payee_type_id || '', email: p.email || '', phone: p.phone || '', address: p.address || '', bank_details: p.bank_details || '', notes: p.notes || '' }); setShowForm(true); };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const sectionStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const btnStyle = { padding: '8px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 };
  const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.85rem', boxSizing: 'border-box' };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };

  if (detail) {
    return (
      <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <button onClick={() => setDetail(null)} style={{ ...btnStyle, background: '#e2e8f0', marginBottom: 16 }}>← Back</button>
        <div style={sectionStyle}>
          <h2 style={{ margin: '0 0 4px', fontSize: '1.3rem', fontWeight: 700 }}>{detail.name}</h2>
          <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{detail.payee_type?.name || 'N/A'} • {detail.email || ''} • {detail.phone || ''}</div>
          {detail.bank_details && <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#475569' }}>🏦 {detail.bank_details}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #ef4444', marginBottom: 0 }}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Expenses</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(detail.total_expenses)}</div></div>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #3b82f6', marginBottom: 0 }}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Total Paid</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(detail.total_paid)}</div></div>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #f97316', marginBottom: 0 }}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Outstanding</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#f97316' }}>{fmt(detail.outstanding)}</div></div>
          <div style={{ ...sectionStyle, borderLeft: '4px solid #ef4444', marginBottom: 0 }}><div style={{ fontSize: '0.75rem', color: '#64748b' }}>Overdue</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(detail.overdue)}</div></div>
        </div>

        {/* Payables */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>📋 Payables</h3>
          {Array.isArray(detail.payables) && detail.payables.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Ref</th><th style={thStyle}>Project</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th><th style={{ ...thStyle, textAlign: 'right' }}>Remaining</th><th style={thStyle}>Due Date</th><th style={thStyle}>Status</th></tr></thead>
                <tbody>
                  {detail.payables.map(p => (
                    <tr key={p.id}><td style={tdStyle}>{p.reference}</td><td style={tdStyle}>{p.project?.name || '-'}</td><td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.final_amount)}</td><td style={{ ...tdStyle, textAlign: 'right', color: '#f97316' }}>{fmt(p.remaining_amount)}</td><td style={tdStyle}>{p.due_date ? new Date(p.due_date).toLocaleDateString() : '-'}</td><td style={tdStyle}>{p.payment_status}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No payables</div>}
        </div>

        {/* Payment History */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 600 }}>💳 Payment History</h3>
          {Array.isArray(detail.payment_history) && detail.payment_history.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Ref</th><th style={thStyle}>Date</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount</th><th style={thStyle}>Method</th><th style={thStyle}>Status</th></tr></thead>
                <tbody>
                  {detail.payment_history.map(t => (
                    <tr key={t.id}><td style={tdStyle}>{t.reference}</td><td style={tdStyle}>{t.transaction_date ? new Date(t.transaction_date).toLocaleDateString() : '-'}</td><td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(t.amount)}</td><td style={tdStyle}>{t.payment_method?.name || '-'}</td><td style={tdStyle}>{t.status}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No payments</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>🏢 Payees / Suppliers</h2>
        <button onClick={() => { setEditing(null); setForm({ name: '', payee_type_id: '', email: '', phone: '', address: '', bank_details: '', notes: '' }); setShowForm(true); }} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>+ New Payee</button>
      </div>

      <div style={sectionStyle}>
        {initialLoading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={thStyle}>Name</th><th style={thStyle}>Type</th><th style={thStyle}>Email</th><th style={thStyle}>Phone</th><th style={{ ...thStyle, textAlign: 'right' }}>Total Expenses</th><th style={{ ...thStyle, textAlign: 'right' }}>Paid</th><th style={{ ...thStyle, textAlign: 'right' }}>Outstanding</th><th style={thStyle}>Actions</th></tr></thead>
              <tbody>
                {payees.length === 0 ? (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No payees found</td></tr>
                ) : payees.map(p => (
                  <tr key={p.id}>
                    <td style={{ ...tdStyle, fontWeight: 500, cursor: 'pointer', color: '#3b82f6' }} onClick={() => viewDetail(p.id)}>{p.name}</td>
                    <td style={tdStyle}>{p.payee_type?.name || '-'}</td>
                    <td style={tdStyle}>{p.email || '-'}</td>
                    <td style={tdStyle}>{p.phone || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.total_expenses)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#3b82f6' }}>{fmt(p.total_paid)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: '#f97316' }}>{fmt(p.total_outstanding)}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => viewDetail(p.id)} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>👁️</button>
                        <button onClick={() => editPayee(p)} style={{ ...btnStyle, background: '#e2e8f0', padding: '4px 10px', fontSize: '0.8rem' }}>✏️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: '90%', maxWidth: 520 }}>
            <h3 style={{ margin: '0 0 20px' }}>{editing ? 'Edit Payee' : 'New Payee'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Type</label><select value={form.payee_type_id} onChange={e => setForm({ ...form, payee_type_id: e.target.value })} style={inputStyle}><option value="">Select</option>{payeeTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Email</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} /></div>
              </div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Address</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Bank Details</label><input value={form.bank_details} onChange={e => setForm({ ...form, bank_details: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b' }}>Notes</label><textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, resize: 'vertical' }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ ...btnStyle, background: '#e2e8f0' }}>Cancel</button>
              <button onClick={savePayee} style={{ ...btnStyle, background: '#3b82f6', color: '#fff' }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
